import { EntityType, EntityInstance, EntityTypeRepository, EntityInstanceRepository } from '../domain/Entities';
import { query_dispatcher_get, sync_command_dispatcher, async_command_dispatcher } from "@/api/client";
import { CommandDispatcher } from "@/modules/shared/domain/CommandDispatcher";
import { QueryDispatcher } from "@/modules/shared/domain/QueryDispatcher";
import { v4 as uuidv4 } from 'uuid';


function objectToDict(obj: any): Record<string, any> {
    const dict: Record<string, any> = {};

    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const value = obj[key];
            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                // Recursively convert nested object
                dict[key] = objectToDict(value);
            } else {
                dict[key] = value;
            }
        }
    }

    return dict;
}

export class EntityTypeApiRepository implements EntityTypeRepository {
    query_dispatcher: QueryDispatcher
    sync_command_dispatcher: CommandDispatcher
    async_command_dispatcher: CommandDispatcher

    constructor() {
        this.query_dispatcher = query_dispatcher_get
        this.sync_command_dispatcher = sync_command_dispatcher
        this.async_command_dispatcher = async_command_dispatcher
    }

    api_to_domain(entity_type) {
        return new EntityType(entity_type.uuid, entity_type.name, entity_type.schema, entity_type.user_id, entity_type.is_visible, entity_type.ui_config, entity_type.kind)
    }

    domain_to_api(entity_type: EntityType) {
        return {
            uuid: entity_type.uuid,
            name: entity_type.name,
            schema: entity_type.schema,
            user_id: entity_type.user_id,
            is_visible: entity_type.is_visible,
            ui_config: entity_type.ui_config,
            kind: entity_type.kind
        }        
    }


    async get_my_types(): Promise<EntityType[]> {
        let res = await this.query_dispatcher.dispatch('get.user.entity.types', {})
        return res.map((entity_type) => this.api_to_domain(entity_type))
    }

    async get_aux_workflow_editor_types(): Promise<EntityType[]> {
        let res = await this.query_dispatcher.dispatch('get.workflow.editor.entity.types', {})
        return res.map((entity_type) => this.api_to_domain(entity_type))
    }

    async get_UI_types(): Promise<EntityType[]> {
        let res = await this.query_dispatcher.dispatch('get.ui.entity.types', {})
        return res.map((entity_type) => this.api_to_domain(entity_type))
    }

    async get_available_UIs(type_id: string): Promise<any> {
        let res = await this.query_dispatcher.dispatch('get.available.uis', {type_uuid: type_id})
        return res
    }

    async get_type_by_id(type_id: string, explicit:boolean=false): Promise<EntityType> {
        let res = await this.query_dispatcher.dispatch('get.entity.type.by.uuid', {type_uuid: type_id, explicit: explicit})
        return this.api_to_domain(res)
    }

    async save(entity_type: EntityType): Promise<any> {
        try {
            let entity_data = this.domain_to_api(entity_type)
            let res = await this.sync_command_dispatcher.dispatch('save.entity.type', {entity_type: entity_data})
            return res['output']
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    create_empty_entity_type(): EntityType {
        let empty_schema = {
            type: 'object',
            properties: {},
            required: []
        }
        const uuid = uuidv4()
        return new EntityType(uuid, '', empty_schema, -1);
    }
}

export class EntityInstanceApiRepository implements EntityInstanceRepository {
    query_dispatcher: QueryDispatcher
    sync_command_dispatcher: CommandDispatcher
    async_command_dispatcher: CommandDispatcher
    constructor() {
        this.query_dispatcher = query_dispatcher_get
        this.sync_command_dispatcher = sync_command_dispatcher
        this.async_command_dispatcher = async_command_dispatcher
    }

    api_to_domain(entity_instance) {
        return new EntityInstance(entity_instance.uuid, entity_instance.entity_type_id, entity_instance.company_id, entity_instance.payload, entity_instance.created, entity_instance.download_date)
    }

    domain_to_api(entity_instance: EntityInstance) {
        return {
            uuid: entity_instance.uuid,
            entity_type_id: entity_instance.entity_type_id,
            company_id: entity_instance.company_id,
            payload: entity_instance.payload,
            created: entity_instance.created
        }
    }

    async get_instances_by_type(company_id: number, entity_type_id: string ,filters:Array<Object>): Promise<EntityInstance[]> {
        let res = await this.query_dispatcher.dispatch('get.instances.by.type', {company_id: company_id, entity_type_id: entity_type_id, filters:filters})
        return res.map((entity_instance) => this.api_to_domain(entity_instance))
    }

    async save(entity_instance: EntityInstance): Promise<EntityInstance> {
        try {
            let entity_data = this.domain_to_api(entity_instance)
            let res = await this.sync_command_dispatcher.dispatch('save.entity.instance', {entity_instance: entity_data})
            return res['output']
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    create_empty_entity_instance(company_id: number, entity_type: EntityType): EntityInstance {
        const uuid = uuidv4();
        let payload = {}
        // for (const property in entity_type.schema['properties']) {
        //     payload[property] = '';
        // }
        return new EntityInstance(uuid, entity_type.uuid, company_id, payload);
    }

    async get_instance_by_id(instance_id: string): Promise<EntityInstance> {
        try {
            let res = await this.query_dispatcher.dispatch('get.entity.instance', {instance_uuid: instance_id})
            return this.api_to_domain(res)
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async get_multiple_instances(instances_ids: string): Promise<EntityInstance> {
        try {
            let res = await this.query_dispatcher.dispatch('get.multiple.entity.instances', {instances_ids: instances_ids})
            let instances = res.map((entity_instance) => this.api_to_domain(entity_instance))
            return instances
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async update_multiple_instances(instances: EntityInstance[]): Promise<void> {
        try {
            let instances_data = instances.map((instance) => this.domain_to_api(instance))
            await this.sync_command_dispatcher.dispatch('update.multiple.entity.instances', {instances: instances_data})
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async delete_instances(instance_ids: string[]): Promise<void> {
        try {
            await this.sync_command_dispatcher.dispatch('delete.instances', {instances_ids: instance_ids})
        } catch (error) {
            console.log(error)
            throw error
        }
    }
    async download_instances(instance_ids: string[]): Promise<any> {
        try {
            let res=  await this.sync_command_dispatcher.dispatch('generate.excel.entity.instances', {entity_ids: instance_ids})
            return res
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async executeWorkflowOnParams(payload, e_type_id, company_id){
        try {
            let res = await this.sync_command_dispatcher.dispatch('execute.workflow.on.params', {payload: payload, entity_type_id: e_type_id, company_id: company_id})
            return res.output
        } catch (error) {
            console.log(error)
            throw error
        }
    }
}


export class MemoryEntityTypeRepository implements EntityTypeRepository {
    types: Array<EntityType> = []

    constructor(data) {
        this.types = data
    }

    async get_my_types(): Promise<EntityType[]> {
        return this.types
    }

    async get_aux_workflow_editor_types(): Promise<EntityType[]> {
        return []
    }

    async get_UI_types(): Promise<EntityType[]> {
        return this.types
    }

    async get_available_UIs(type_id: string): Promise<any> {
        return []
    }

    async get_type_by_id(type_id: string): Promise<EntityType> {
        return this.types.find(t => t.uuid == type_id)
    }

    async save(entity_type: EntityType): Promise<any> {
        return entity_type
    }

    create_empty_entity_type(): EntityType {
        return new EntityType(uuidv4(), '', {type: 'object', properties: {}, required: []}, -1);
    }
}